import { useMemo, useState, useEffect } from "react";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import {
  getPublishedPortalData,
  listFavorites,
  shareCampaign,
  shareContent,
  toggleFavorite,
} from "lib/api/franchise-marketing-api";
import { INITIAL_FILTERS } from "../constants";
import {
  filterMarketingContents,
  getFavoriteKey,
  getUniqueFieldValues,
  matchesDestaqueTab,
  isMaterialType,
} from "../utils";

function mapFavoritesFromApi(items = []) {
  return items.map((item) => `${item.EntidadeTipo}:${item.EntidadeId}`);
}

export default function useFranchiseMarketing() {
  const snackbar = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [portalContents, setPortalContents] = useState([]);
  const [heroCampaigns, setHeroCampaigns] = useState([]);
  const [search, setSearch] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [destaqueTab, setDestaqueTab] = useState("Banner");
  const [showAllDestaques, setShowAllDestaques] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  useEffect(() => {
    setShowAllDestaques(false);
  }, [destaqueTab]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [data, favoriteItems] = await Promise.all([
          getPublishedPortalData(),
          listFavorites().catch(() => []),
        ]);
        setPortalContents(data.portalContents || []);
        setHeroCampaigns(data.heroCampaigns || []);
        setFavorites(mapFavoritesFromApi(favoriteItems));
      } catch (error) {
        console.error(error);
        snackbar.showMessage(
          error?.message || "Falha ao carregar conteúdos de marketing.",
          { severity: "error" },
        );
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const categoriasOptions = useMemo(
    () => getUniqueFieldValues(portalContents, "categoria"),
    [portalContents],
  );

  const campanhasOptions = useMemo(
    () => getUniqueFieldValues(portalContents, "campanha"),
    [portalContents],
  );

  const filteredContents = useMemo(
    () => filterMarketingContents(portalContents, search, filters, favorites),
    [portalContents, search, filters, favorites],
  );

  const destaquesFiltrados = useMemo(
    () =>
      filteredContents.filter((item) =>
        matchesDestaqueTab(item.tipo, destaqueTab),
      ),
    [filteredContents, destaqueTab],
  );

  const destaques = useMemo(
    () => (showAllDestaques ? destaquesFiltrados : destaquesFiltrados.slice(0, 3)),
    [destaquesFiltrados, showAllDestaques],
  );

  const videos = useMemo(
    () => filteredContents.filter((item) => item.tipo === "Video").slice(0, 4),
    [filteredContents],
  );

  const materiaisRecentes = useMemo(
    () =>
      filteredContents
        .filter((item) => isMaterialType(item.tipo))
        .slice(0, 3),
    [filteredContents],
  );

  const toggleFilterArray = (field, value) => {
    setFilters((prev) => {
      const current = prev[field];
      const next = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return { ...prev, [field]: next };
    });
  };

  const clearFilters = () => {
    setSearch("");
    setFilters(INITIAL_FILTERS);
  };

  const handleDownload = (item) => {
    const url = item.arquivoUrl || item.thumbnail;
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
      snackbar.showMessage(`Download iniciado: ${item.titulo}`, {
        severity: "success",
      });
      return;
    }
    snackbar.showMessage("Arquivo não disponível para download.", {
      severity: "warning",
    });
  };

  const handleShare = async (item) => {
    try {
      const result = item.isCampaign
        ? await shareCampaign(item.id)
        : await shareContent(item.id);
      const token = result?.TokenCompartilhamento;
      if (token) {
        await navigator.clipboard.writeText(token);
        snackbar.showMessage(`Link copiado: ${item.titulo}`, {
          severity: "success",
        });
        return;
      }
      snackbar.showMessage("Link de compartilhamento não disponível.", {
        severity: "warning",
      });
    } catch (error) {
      snackbar.showMessage(error?.message || "Falha ao compartilhar.", {
        severity: "error",
      });
    }
  };

  const handleFavorite = async (item) => {
    const key = getFavoriteKey(item);
    const entidadeTipo = item.itemTipo || (item.isCampaign ? "Campanha" : "Conteudo");

    try {
      const result = await toggleFavorite(entidadeTipo, item.id);
      const isFavorite = !!result?.IsFavorite;

      setFavorites((prev) => {
        if (isFavorite) {
          return prev.includes(key) ? prev : [...prev, key];
        }
        return prev.filter((favoriteKey) => favoriteKey !== key);
      });

      snackbar.showMessage(
        result?.MessageResponse ||
          (isFavorite ? "Adicionado aos favoritos" : "Removido dos favoritos"),
        { severity: "info" },
      );
    } catch (error) {
      snackbar.showMessage(error?.message || "Falha ao atualizar favoritos.", {
        severity: "error",
      });
    }
  };

  const handleViewCampaign = (campanhaId) => {
    const campanha = portalContents.find(
      (item) => item.id === campanhaId && item.isCampaign,
    );
    if (campanha) setPreviewItem(campanha);
  };

  const handleToggleShowAllDestaques = () => {
    setShowAllDestaques((prev) => !prev);
  };

  const cardHandlers = {
    onView: setPreviewItem,
    onDownload: handleDownload,
    onShare: handleShare,
    onFavorite: handleFavorite,
    isFavorite: (item) => favorites.includes(getFavoriteKey(item)),
  };

  return {
    loading,
    search,
    setSearch,
    filtersOpen,
    setFiltersOpen,
    destaqueTab,
    setDestaqueTab,
    showAllDestaques,
    destaquesFiltrados,
    previewItem,
    setPreviewItem,
    filters,
    setFilters,
    categoriasOptions,
    campanhasOptions,
    heroCampaigns,
    destaques,
    videos,
    materiaisRecentes,
    toggleFilterArray,
    clearFilters,
    handleViewCampaign,
    handleToggleShowAllDestaques,
    cardHandlers,
    snackbar,
  };
}
